import React, { useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';

import { setLiveEventSessionPlayerType } from '../../../../store/actions/event/event-actions';
import { getEnabledReactions, getReactionsEnabled, getVideoPlayerType, getShouldShowPlaceholder } from './video/utils';
import { useIsAdmin } from 'hooks/context.hooks';
import { useTypedSelector } from 'store/reducers/use-typed-selector';
import { selectAttendeeInPersonEnabled } from 'store/selectors/live-event';

// Import from companion .ts using explicit extension to avoid circular resolution.
import {
	Actions,
	VideoControlsEvents,
	VideoStateContext,
	getVideoPlayerInitialState,
	videoPlayerReducer,
} from './session-stream-provider.ts';

import type { VideoPlayerAction, VideoPlayerReducerType, SessionStreamStateProps } from './session-stream-provider.ts';

export type SessionStreamProviderProps = {
	children: JSX.Element | JSX.Element[] | ((state: VideoPlayerReducerType, dispatch: React.Dispatch<VideoPlayerAction>) => React.ReactNode | React.ReactNode[]);
} & SessionStreamStateProps;

export const SessionStreamProvider: React.FC<SessionStreamProviderProps> = ({
	children,
	...props
}) => {
	const isAdmin = useIsAdmin();
	const attendeeInPersonEnabled = useTypedSelector(selectAttendeeInPersonEnabled);
	const [state, dispatch] = useReducer(videoPlayerReducer, getVideoPlayerInitialState(props, isAdmin, attendeeInPersonEnabled));
	const reduxDispatch = useDispatch();
	const {
		isLive,
		event,
		session,
		language,
		isEditor,
		onDemandVideo,
		streamStartTime,
		manualPip,
		overlayOpen,
		moderatorView,
		fsMeetData,
		currentDynamicVideo,
	} = props;

	useEffect(() => {
		dispatch({ type: Actions.SetStreamStartTime, payload: streamStartTime });
	}, [streamStartTime]);

	useEffect(() => {
		if (!isLive) {
			dispatch({ type: Actions.SetStreamStartTime, payload: undefined });
		}
	}, [isLive]);

	useEffect(() => {
		dispatch({ type: Actions.SetModeratorView, payload: !!moderatorView });
	}, [moderatorView]);

	useEffect(() => {
		dispatch({
			type: Actions.SetPlayerTypeAndStateUpdate,
			payload: {
				playerType: getVideoPlayerType({
					isLive,
					event,
					session,
					attendeeInPersonEnabled,
					language,
					isEditor,
					currentDynamicVideo,
					fsMeetData,
					moderatorView,
					onDemandVideo
				}),
				showPlaceholder: getShouldShowPlaceholder({
					isLive,
					event,
					session,
					attendeeInPersonEnabled,
					language,
					isEditor,
					currentDynamicVideo,
					fsMeetData,
					onDemandVideo
				}),
				isLive,
				currentDynamicVideo
			}
		});
	}, [isLive, event, session, language, isEditor, onDemandVideo, fsMeetData, currentDynamicVideo, moderatorView]);

	useEffect(() => {
		reduxDispatch(setLiveEventSessionPlayerType(state.playerType));
	}, [reduxDispatch, state.playerType]);

	useEffect(() => {
		dispatch({ type: Actions.SetSession, payload: session });
	}, [session]);

	useEffect(() => {
		dispatch({ type: Actions.SetVideo, payload: props.onDemandVideo });
	}, [props.onDemandVideo]);

	// player type has changed, likely vid has gone live or replay began
	useEffect(() => {
		dispatch({ type: Actions.ResetIntialPlay, payload: false });
	}, [state.playerType]);

	useEffect(() => {
		dispatch({ type: Actions.SetIsMobile, payload: props.isMobile });
	}, [props.isMobile]);

	useEffect(() => {
		dispatch({ type: Actions.SetChatIsOverlaid, payload: props.chatIsOverlaid });
	}, [props.chatIsOverlaid]);

	useEffect(() => {
		dispatch({ type: Actions.SetChatClosed, payload: props.chatClosed });
	}, [props.chatClosed]);

	useEffect(() => {
		dispatch({ type: Actions.SetReactionsEnabled, payload: getReactionsEnabled(props.session.reaction_settings) });
		dispatch({ type: Actions.SetReactions, payload: getEnabledReactions(props.session.reaction_settings) });
	}, [props.session.reaction_settings]);

	useEffect(() => {
		dispatch({ type: Actions.SetVideo, payload: props.onDemandVideo });
	}, [props.onDemandVideo]);

	useEffect(() => {
		if (props.hasScrolledPast) {
			dispatch({ type: Actions.CancelPip, payload: false });
		}
	}, [props.hasScrolledPast]);

	useEffect(() => {
		dispatch({ type: Actions.SetManualPip, payload: manualPip });
	}, [manualPip]);

	useEffect(() => {
		if (overlayOpen) {
			dispatch({ type: Actions.SetOverlayOpen, payload: overlayOpen });
		}
	}, [overlayOpen]);

	useEffect(() => {
		localStorage.setItem('volume', state.volume.toString());
	}, [state.volume]);

	useEffect(() => {
		localStorage.setItem('muted', state.muted.toString());
	}, [state.muted]);

	const store = useMemo(() => {
		return { state, dispatch, eventTarget: VideoControlsEvents };
	}, [state]);

	const content = () => {
		if (typeof children === "function") {
			return children(state, dispatch);
		}

		return children;
	};

	return (
		<VideoStateContext.Provider value={store}>
			{content()}
		</ VideoStateContext.Provider>
	);
};

// SessionStreamProvider.whyDidYouRender = true;
